import React, { Fragment, FC, useEffect, useState } from 'react'
import { Route, Link } from 'react-router-dom'
import track, { TrackingProp } from 'react-tracking'

import BEMHelper from '@ynap/bem-helper'
import { Button, SimpleAsyncButton } from '@ynap/button'
import { CreditCardForm } from '@ynap/credit-card-form'
import EmptyList from '@ynap/empty-list'
import Notification from '@ynap/notification'
import Overlay from '@ynap/overlay'

import { walletActions } from 'app/state/wallet-state'
import { Wallet, Card } from 'app/state/wallet-state/state'
import { myaccountPageData } from '../../analytics'
import { trackDeleteCardSuccess } from '../analytics'
import CardComponent from '../Card/Card'
import CardSkeleton from '../CardSkeleton/CardSkeleton'

const bem = new BEMHelper('CardWallet')

const CardWallet: FC<CardWalletProps> = (props) => {
    const {
        className,
        tracking,
        messages,
        wallet,
        fetchWallet,
        addCard,
        updateCard,
        deleteCard,
        resetLoadError,
        skeletonCards = 3,
        config,
        locale,
        push,
        nativeApp,
    } = props

    const [tokenToDelete, setTokenToDelete] = useState<string>()
    const [showNotification, setShowNotification] = useState<string>()
    const [submitError, setSubmitError] = useState<string | boolean | undefined>()
    const [cardLastFour, setCardLastFour] = useState<string>()

    const { fullPageHeader } = config?.head?.flags || {}
    const { cards, fetching, error: walletError, loading } = wallet

    const cardToDelete = !!tokenToDelete && cards?.find(({ token }) => tokenToDelete === token)

    useEffect(() => {
        tracking &&
            tracking.trackEvent(
                myaccountPageData({
                    name: 'saved cards',
                    subCategory1: 'saved cards',
                })(props)
            )
        fetchWallet?.()
    }, [])

    function handleCardFormOverlay() {
        setShowNotification(undefined)
        setSubmitError(undefined)
    }

    function handleAddCard(values) {
        const lastFourDigits = values.number
            .replace(/\s/g, '') // removes white spaces
            .slice(-4)

        addCard(values)
            .then((res) => {
                if (!res?.error) {
                    setShowNotification('ADD')
                    setCardLastFour(lastFourDigits)
                    push(`/${locale.id}/account/saved-cards`)
                    fetchWallet?.()
                } else {
                    setSubmitError(res?.error)
                }
            })
            .catch(() => {
                setSubmitError(walletError)
                push(`/${locale.id}/account/saved-cards`)
            })
    }

    function handleEditCard(values) {
        updateCard(values)
            .then((res) => {
                if (!res?.error) {
                    setShowNotification('EDIT')
                    setCardLastFour(values.lastFourDigits)
                    push(`/${locale.id}/account/saved-cards`)
                    fetchWallet?.()
                } else {
                    setSubmitError(res?.error)
                }
            })
            .catch(() => {
                setSubmitError(walletError)
                push(`/${locale.id}/account/saved-cards`)
            })
    }

    function handleDeleteOverlay(card: Card) {
        setShowNotification(undefined)
        resetLoadError(card.token)
        tokenToDelete && resetLoadError(tokenToDelete)
        const newCardToDelete = !tokenToDelete || (tokenToDelete && tokenToDelete !== card.token) ? card : undefined
        setTokenToDelete(newCardToDelete?.token)
        setCardLastFour(newCardToDelete?.lastFourDigits)
    }

    function handleDeleteCard() {
        tracking && tracking.trackEvent(trackDeleteCardSuccess())
        tokenToDelete && deleteCard({ cardToken: tokenToDelete })
        setShowNotification('DELETE')
    }

    return (
        <div className={bem(null, null, className)}>
            {walletError ? (
                <EmptyList
                    className={bem('emptyState')}
                    icon={<div className={bem('errorIcon')} />}
                    variant={nativeApp ? '' : 'myAccountError'}
                    title={messages.cardWallet.pageError()}
                >
                    <Button onClick={fetchWallet} type="primary-link" className={bem('emptyLink')}>
                        {messages.cardWallet.retryButton()}
                    </Button>
                </EmptyList>
            ) : (
                <Fragment>
                    {!fetching && !cards?.length && cards !== undefined ? (
                        <EmptyList
                            className={bem('emptyState')}
                            variant={nativeApp ? '' : 'myAccountError'}
                            icon={<div className={bem('emptyStateIcon')} />}
                            title={messages.cardWallet.emptyListTitle()}
                            details={messages.cardWallet.emptyListDetails()}
                        >
                            <Button
                                type="primary"
                                component={Link}
                                className={bem('addCardButton')}
                                onClick={handleCardFormOverlay}
                                to={`/${locale.id}/account/saved-cards/add`}
                                data-action="add"
                            >
                                {messages.cardWallet.addCard()}
                            </Button>
                        </EmptyList>
                    ) : (
                        <Fragment>
                            <div className={bem('header')}>
                                {!fullPageHeader && <div className={bem('title')}>{messages.cardWallet.title()}</div>}

                                {showNotification && (
                                    <Notification
                                        type={'success'}
                                        className={bem('success')}
                                        message={messages.cardWallet.success({
                                            method: showNotification,
                                            cardLastFour,
                                        })}
                                    />
                                )}

                                {!fullPageHeader && (
                                    <div className={bem('description')}>{messages.cardWallet.description()}</div>
                                )}
                            </div>

                            <ul className={bem('cardList')}>
                                <li className={bem('addCardWrapper')}>
                                    <Button
                                        component={Link}
                                        className={bem('addCardListButton')}
                                        onClick={handleCardFormOverlay}
                                        to={`/${locale.id}/account/saved-cards/add`}
                                        data-action="add"
                                    >
                                        <div className={bem('addCardContent')}>
                                            <div className={bem('addCardImage')} />
                                            <div className={bem('addCardText')}>{messages.cardWallet.addCard()}</div>
                                        </div>
                                    </Button>
                                </li>
                                {!fetching && cards
                                    ? cards?.map((card, index) => (
                                          <div key={index} className={bem('cardWrapper')}>
                                              <CardComponent {...card} messages={messages}>
                                                  <Button
                                                      component={Link}
                                                      className={bem('cardActionBtn', 'edit')}
                                                      onClick={handleCardFormOverlay}
                                                      to={`/${locale.id}/account/saved-cards/edit/${card.token}`}
                                                  >
                                                      {messages.cardWallet.edit()}
                                                  </Button>
                                                  <Button
                                                      className={bem('cardActionBtn', 'delete')}
                                                      onClick={() => handleDeleteOverlay(card)}
                                                  >
                                                      {messages.cardWallet.delete()}
                                                  </Button>
                                              </CardComponent>
                                          </div>
                                      ))
                                    : Array(skeletonCards)
                                          .fill(undefined)
                                          .map((o, index) => (
                                              <li key={index} className={bem('cardWrapper')}>
                                                  <CardSkeleton />
                                              </li>
                                          ))}
                            </ul>
                        </Fragment>
                    )}

                    {cardToDelete ? (
                        <Overlay
                            className={bem('deleteModal')}
                            title={messages.cardWallet.deleteTitle()}
                            type="alert"
                            handleBodyClick={() => handleDeleteOverlay(cardToDelete)}
                        >
                            <div className={bem('deletePrompt')}>
                                {messages.cardWallet.deletePrompt({
                                    cardNumber: cardToDelete.lastFourDigits,
                                })}
                            </div>
                            <div className={bem('deleteActions')}>
                                <Button className={bem('cancel')} onClick={() => handleDeleteOverlay(cardToDelete)}>
                                    {messages.cardWallet.cancel()}
                                </Button>
                                <SimpleAsyncButton
                                    className={bem('delete')}
                                    onClick={() => handleDeleteCard()}
                                    loading={cardToDelete.loading}
                                    copy={messages.cardWallet.delete()}
                                />
                            </div>
                            {cardToDelete.loadError ? (
                                <Notification
                                    className={bem('deleteError')}
                                    type="error"
                                    message={messages.cardWallet.deleteError({
                                        cardNumber: cardToDelete.lastFourDigits,
                                    })}
                                />
                            ) : null}
                        </Overlay>
                    ) : (
                        ''
                    )}
                </Fragment>
            )}

            <Route
                path="/:locale/account/saved-cards/add"
                render={() => (
                    <Overlay
                        className={bem('cardFormOverlay')}
                        title={messages.creditCardForm.title({ method: 'ADD' })}
                        onClose={() => push(`/${locale.id}/account/saved-cards`)}
                    >
                        <CreditCardForm
                            messages={messages}
                            className={bem('addCardForm')}
                            onSubmit={(values) => handleAddCard(values)}
                            loading={loading}
                            toolTipError={submitError}
                            locale={locale}
                            config={config}
                            hideSaveCard
                            isCardWallet
                        />
                    </Overlay>
                )}
            />

            <Route
                path="/:locale/account/saved-cards/edit/:id"
                render={(matchProps) => {
                    const card = cards?.find((card) => card.token === matchProps.match.params.id)

                    if (!card) return null

                    const monthString = card.expiry.month
                    const expiryMonth = monthString.length === 1 ? `0${monthString}` : monthString

                    return (
                        <Overlay
                            className={bem('cardFormOverlay')}
                            title={messages.creditCardForm.title({ method: 'EDIT' })}
                            onClose={() => push(`/${locale.id}/account/saved-cards`)}
                        >
                            <CreditCardForm
                                messages={messages}
                                className={bem('addCardForm')}
                                onSubmit={(values) => handleEditCard(values)}
                                loading={loading}
                                toolTipError={submitError}
                                locale={locale}
                                config={config}
                                hideSaveCard
                                isCardWallet
                                initialValues={{
                                    cardToken: card.token,
                                    lastFourDigits: card.lastFourDigits,
                                    cardHolderName: card.card_holder.name,
                                    expiryDateStr: `${expiryMonth}/${card.expiry.year}`,
                                }}
                            />
                        </Overlay>
                    )
                }}
            />
        </div>
    )
}

export default track({})(CardWallet)

type BoundActionCreator<T extends (...args: any) => any> = (...args: Parameters<T>) => ReturnType<ReturnType<T>>

type I18nFunction = (...args: any) => string

type CardWalletProps = {
    messages: {
        cardWallet: {
            title: I18nFunction
            description: I18nFunction
            delete: I18nFunction
            cancel: I18nFunction
            success: I18nFunction
            deletePrompt: I18nFunction
            deleteError: I18nFunction
            deleteTitle: I18nFunction
            emptyListDetails: I18nFunction
            emptyListTitle: I18nFunction
            addCard: I18nFunction
            edit: I18nFunction
            pageError: I18nFunction
            retryButton: I18nFunction
        }
        creditCardForm: {
            title: I18nFunction
            number: I18nFunction
            cvv: I18nFunction
            cardHolderName: I18nFunction
            expiryDate: I18nFunction
            month: I18nFunction
            monthPlaceholder: I18nFunction
            year: I18nFunction
            yearPlaceholder: I18nFunction
            cvvHelp: I18nFunction
            saveCard: I18nFunction
            submit: I18nFunction
            cardDetails: I18nFunction
            cardNumberFormat: I18nFunction
            expiryDateFormat: I18nFunction
            cvvFormat: I18nFunction
            privacyPolicy: I18nFunction
            acceptedCards: I18nFunction
            error: I18nFunction
        }
        card: {
            expiry: I18nFunction
            expired: I18nFunction
            cardHolder: I18nFunction
        }
        errors: {
            required: I18nFunction
            invalid: I18nFunction
            maxLength: I18nFunction
        }
    }
    fetchWallet?: BoundActionCreator<typeof walletActions.fetchWallet>
    addCard: BoundActionCreator<typeof walletActions.addCard>
    updateCard: BoundActionCreator<typeof walletActions.addCard>
    deleteCard: BoundActionCreator<typeof walletActions.deleteCard>
    resetLoadError: BoundActionCreator<typeof walletActions.resetLoadError>
    deleteCardError?: boolean
    wallet: Wallet
    className?: string
    fetchWalletError?: boolean
    skeletonCards?: number
    userInitials?: string
    userSummary?: any
    tracking?: TrackingProp
    config?: any
    locale: {
        id: string
    }
    push: (path: string) => void
    nativeApp: string | undefined
}
